import { useCallback, useSyncExternalStore } from 'react';
import { cookieConsent, onCategoryDenied } from '#/lib/consent';

// BACKEND: CF-10 -- ids only; enriched via a findByIds bulk query once it exists.
const COOKIE_NAME = 'saved_place_ids';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const EMPTY: string[] = [];
let cache: { raw: string | null; value: string[] } = {
  raw: null,
  value: EMPTY,
};

function readCookie(): string[] {
  if (typeof document === 'undefined') return EMPTY;
  const entry = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${COOKIE_NAME}=`));
  const raw = entry ? entry.slice(COOKIE_NAME.length + 1) : null;
  if (raw === cache.raw) return cache.value;

  if (raw === null) {
    cache = { raw, value: EMPTY };
    return cache.value;
  }
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    cache = {
      raw,
      value:
        Array.isArray(parsed) && parsed.every((id) => typeof id === 'string')
          ? parsed
          : EMPTY,
    };
  } catch {
    cache = { raw, value: EMPTY };
  }
  return cache.value;
}

function writeCookie(ids: string[]): void {
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is not supported in Safari/Firefox yet
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(ids))}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
}

function clearCookie(): void {
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is not supported in Safari/Firefox yet
  document.cookie = `${COOKIE_NAME}=; max-age=0; path=/; SameSite=Lax`;
}

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(): void {
  for (const listener of listeners) listener();
}

onCategoryDenied('functional', () => {
  clearCookie();
  notify();
});

export function useSaved() {
  const ids = useSyncExternalStore(subscribe, readCookie, () => EMPTY);

  const has = useCallback((id: string) => ids.includes(id), [ids]);

  const toggle = useCallback((id: string) => {
    if (!cookieConsent.isAllowed('functional')) return;

    const current = readCookie();
    writeCookie(
      current.includes(id)
        ? current.filter((entry) => entry !== id)
        : [id, ...current],
    );
    notify();
  }, []);

  return { ids, count: ids.length, has, toggle };
}
